import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import ReactGA from 'react-ga'
import { postToApiWithoutToken } from 'actions'
import {
   makeStyles,
   Typography,
   Grid,
   FormControlLabel,
   Checkbox,
   Hidden,
   FormHelperText
} from '@material-ui/core'
import Recaptcha from 'react-recaptcha'
import moment from 'moment'

import EmailField from '../../form/EmailField'
import PasswordField from '../../form/PasswordField'
import SubmitButton from '../../form/SubmitButton'
import ButtonLink from 'components/commons/ButtonLink'
import PortalModal, { usePortalModal } from 'components/commons/PortalModal'
import ProfileSelector from '../ProfileSelector'

import { getAuthorizationDataAction } from '../actions'
import { useStateValue } from 'context/store'
import { useFormValidation } from 'utils/customHooks/useFormValidation'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { formUtils } from 'utils/utils'
import { REGEXP, LOCALSTORAGE_KEYS } from 'utils/constants'
import logoMutualMobile from 'images/logo-only.png'
import { closeSpinnerAction, openSpinnerAction } from 'context/reducers/openSpinnerReducer'
import { openSnackBarAction } from 'context/reducers/openSnackbarReducer'
import { logoutUser } from 'actions/loginActions'
import { loadState } from 'utils/localStorage'

// --------------------

const useLoginFormStyles = makeStyles(theme => ({
   loginFormContainer: {
      height: '100%',
      display: props => props.entities?.length ? 'none' :'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      margin: theme.spacing(0, 2)
   },
   formTitle: {
      fontSize: '22px',
      textAlign: 'center',
      margin: theme.spacing(2, 0)
   },
   logoMobile: {
      width: 100,
      alignSelf: 'center',
      marginTop: theme.spacing(2)
   },
   extraButtons: {
      marginBottom: theme.spacing(2)
   },
   formFieldsContainer: {
      marginBottom: theme.spacing(5)
   },
   recaptcha: {
      flexDirection: 'column',
      justifySelf: 'center', 
      margin: '13px 0px'
   }
}))

const INITIAL_LOGIN_FORM_STATE = {
   email: '',
   password: '',
   recaptcha: ''
}

const INITIAL_LOGIN_FORM_VALIDATION = {
   email: false,
   password: false,
   recaptcha: false
}

const isRecaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED !== 'false'

const { notRequired, doesNotMatchRegexp } = formUtils
const isInvalidIf = {
   email: value =>
      notRequired(value) || doesNotMatchRegexp(value, REGEXP.EMAIL),
   /*
		NOTA: En este password NO se valida la REGEXP, solo se valida al momento de crearla,
		pq hay usuarios internos cuya contraseña no necesariamente deben cumplir esas validaciones.
	*/
   password: value => notRequired(value),
   recaptcha: isRecaptchaEnabled ? notRequired : () => false
}

const handleRecaptcha = (mutationFn, clear) => value =>{
   mutationFn({ target: {name: 'recaptcha', value: clear ? '' : value}}) 
}

const modalOptions = {
   message: "Hemos añadido la opción reCAPTCHA para responder a la política de seguridad de la información de la organización.",
   confirmButtonText: 'entendido',
   hideCloseButton: true,
   iconColor: 'info',
   icon: 'info'
}

const shouldShowModal = !moment().isAfter('2025/07/31')
const INITIAL_ENTITY_STATE = []

function LoginForm({ history }) {
   const [_, dispatch] = useStateValue()
   const [formData, setFormData] = useState(INITIAL_LOGIN_FORM_STATE)
   const [isInvalid, setIsInvalid] = useState(INITIAL_LOGIN_FORM_VALIDATION)
   const [autocompleteForm, setAutocompleteForm] = useState(false)
   const [entities, setEntities] = useState(INITIAL_ENTITY_STATE)
   const classes = useLoginFormStyles({ entities })
   const {params, onClose, setModal} = usePortalModal()

   const { setValue, isAnyFieldInvalid } = useFormValidation(
      [formData, setFormData],
      setIsInvalid,
      isInvalidIf
   )

   // ----------- Authorization ----------------

   const getAuthorizationData = async entity => {
      dispatch(openSpinnerAction())
      try {
         await getAuthorizationDataAction(dispatch, history, entity)
      } catch (error) {
         if(error?.status == 402) {
            const mapEntities = error?.data?.map(
               ({ name, entidad }) => ({ label: `${entidad} ${name}`, value: entidad })
            )
            setEntities(mapEntities)
         } 
         else dispatch(openSnackBarAction(error?.response?.message))
      } finally {
         dispatch(closeSpinnerAction())
      }
   } 

   // ----------- Login  ----------------
   async function _loginUser() {
      // NOTA: Este spinner se cierra dentro del getAuthorizationDataAction()
      dispatch({ type: 'OPEN_SPINNER' })
      const payload = {
         username: formData.email,
         password: formData.password
      }
      const url = `${process.env.REACT_APP_LOGIN_API}/users/login`
      const data = await postToApiWithoutToken(url, payload)
      if (data) {
         ReactGA.event({
            category: 'LOGIN',
            action: 'LOGIN_SUCCESS_PZS'
         })
         localStorage.setItem(LOCALSTORAGE_KEYS.TOKEN_APP, data.access_token) // Requerido para microfrontends
         dispatch({
            type: 'LOGIN_SESSION',
            session: data,
            authenticated: true,
            accessToken: data.access_token
         })
         getAuthorizationData()
      }
   }

   const loginUser = useApiRequest(_loginUser, 'Login: Loguear Usuario', false)

   // ----------- Form handling ----------------
   function onSubmit(event) {
      event.preventDefault() // To avoid page reload by default
      if (!isAnyFieldInvalid()) {
         loginUser()
      }
   }

   function onForgotPassword() {
      history.push('request-password-reset')
   }

   function onThirdRegister() {
      history.push('user-register')
   }

   function onAutocompleteFormChange(event) {
      setAutocompleteForm(event.target.checked)
   }

   async function onBackToLogin() {
      const refresh_token = loadState()?.token?.refresh_token
      logoutUser(dispatch, refresh_token)
      setEntities(INITIAL_ENTITY_STATE)
   }

   async function onSelectEntity(data) { 
      dispatch(openSpinnerAction())
      await getAuthorizationData(data?.entity)
      dispatch(closeSpinnerAction())
   }

   useEffect(() => {
      // NOTA: la key 'autocomplete_form' NO está en las constantes pq NO se debe borrar al hacer logout
      const savedAutocompleteForm = localStorage.getItem('autocomplete_form')
      setAutocompleteForm(savedAutocompleteForm === 'true')
      if (isRecaptchaEnabled) {
         shouldShowModal && setModal(modalOptions)
      }
   }, [])

   useEffect(() => {
      localStorage.setItem('autocomplete_form', autocompleteForm.toString())
   }, [autocompleteForm])

   return (
      <> 
      <ProfileSelector entities={entities} onSelect={onSelectEntity} onExit={onBackToLogin} />
      <Grid className={classes.loginFormContainer}>
         <Hidden smUp>
            <img
               src={logoMutualMobile}
               alt="Logo MutualSer"
               className={classes.logoMobile}
            />
         </Hidden>
         <Typography className={classes.formTitle}>
            Identifícate en Mutual SER
         </Typography>
         <form
            onSubmit={onSubmit}
            autoComplete={autocompleteForm ? 'on' : 'off'}
         >
            <Grid className={classes.formFieldsContainer}>
               <EmailField
                  formValue={formData?.email}
                  fieldInvalid={isInvalid?.email}
                  handleChange={setValue}
               />
               <PasswordField
                  fieldLabel="Contraseña *"
                  fieldName="password"
                  formValue={formData?.password}
                  handleChange={setValue}
                  fieldInvalid={isInvalid?.password}
                  errorMessage={
                     !formData?.password.length &&
                     isInvalid?.password &&
                     'El campo Contraseña es obligatorio'
                  }
               />
               {isRecaptchaEnabled && (
                  <Grid className={classes.recaptcha}>
                     <Recaptcha
                        key='login-recaptcha'
                        sitekey={process.env.REACT_APP_RECAPTCHA_KEY} 
                        render="explicit"
                        onloadCallback={() => true} 
                        verifyCallback={handleRecaptcha(setValue)}
                        expiredCallback={handleRecaptcha(setValue, true)}
                        hl='es-419'
                     />
                     {isInvalid?.recaptcha &&
                        <FormHelperText error>
                           La validacion del captcha es requerida
                        </FormHelperText>}
                  </Grid>
               )}
               <FormControlLabel
                  control={
                     <Checkbox
                        name="remember"
                        checked={autocompleteForm}
                        color="primary"
                        onChange={onAutocompleteFormChange}
                     />
                  }
                  label="Recordarme"
               />
            </Grid>
            <SubmitButton text="INGRESAR" />
         </form>
         <div className={classes.extraButtons}>
            <ButtonLink
               text="¿OLVIDASTE TU CLAVE DE ACCESO?"
               onClick={onForgotPassword}
            />
            <ButtonLink
               text="SOLICITUD DE REGISTRO"
               onClick={onThirdRegister}
            />
         </div>
         <PortalModal params={params} onClose={onClose} />
      </Grid>
      </>
   )
}

export default withRouter(LoginForm)
